import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import usePageMeta from '@/hooks/usePageMeta';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RichText from '@/components/RichText';
import { Badge } from '@/components/ui/badge';
import {
  Building2, MapPin, Users, Globe, ExternalLink, Mail, Linkedin,
  ArrowLeft, Calendar, Leaf, Award, User, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';
import MemberProfileModal from '@/components/MemberProfileModal';
import MemberAvatar from '@/components/MemberAvatar';

const BACKEND_URL = '';

function getFaviconUrl(website) {
  if (!website) return null;
  try {
    const domain = website.replace(/^https?:\/\//, '').split('/')[0];
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  } catch { return null; }
}

export default function CompanyProfilePage({ user }) {
  const { sponsorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Honour the originating directory passed via router state — e.g. when the
  // user clicked through from the Company directory, "Back" should return
  // there rather than the default /partners list.
  const backTo = location.state?.from || '/partners';
  const backLabel = backTo === '/companies' ? 'Back to Company Directory' : 'Back to Directory';
  const [partner, setPartner] = useState(null);
  usePageMeta(partner ? {
    title: partner.name || partner.organisation || 'Partner',
    description: (partner.description || partner.summary || '').slice(0, 200),
    image: partner.logo_url,
  } : { title: 'Partner' });
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  // Team-member click opens an in-place modal (so the user can close it and
  // remain on the partner page) — much better UX than navigating to a
  // dedicated /members/{id} route that doesn't exist.
  const [selectedMember, setSelectedMember] = useState(null);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    const load = async () => {
      try {
        const [partnerRes, docsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/partners/${sponsorId}`, { credentials: 'include', headers }),
          fetch(`${BACKEND_URL}/api/partners/${sponsorId}/documents`, { credentials: 'include', headers }),
        ]);
        if (partnerRes.ok) setPartner(await partnerRes.json());
        else { toast.error('Partner not found'); navigate(backTo); }
        if (docsRes.ok) setDocuments(await docsRes.json());
      } catch { toast.error('Failed to load'); navigate(backTo); }
      setLoading(false);
    };
    load();
  }, [sponsorId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="flex justify-center pt-32"><div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" /></div>
    </div>
  );
  if (!partner) return null;

  const typeColor = { Sponsor: 'bg-brand-navy', Exhibitor: 'bg-brand-sage', Partner: 'bg-slate-600' };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <Button variant="ghost" size="sm" onClick={() => navigate(backTo)} className="mb-4" data-testid="back-to-directory">
            <ArrowLeft className="h-4 w-4 mr-1" /> {backLabel}
          </Button>

          {/* Hero Card */}
          <Card className="overflow-hidden mb-6" data-testid="partner-profile-card">
            <div className="h-3 bg-gradient-to-r from-brand-navy to-brand-sage" />
            <div className="p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden border border-slate-200">
                  {partner.logo_url ? (
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }}
                    />
                  ) : getFaviconUrl(partner.website) ? (
                    <img
                      src={getFaviconUrl(partner.website)}
                      alt={partner.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full items-center justify-center"
                    style={(partner.logo_url || getFaviconUrl(partner.website)) ? { display: 'none' } : { display: 'flex' }}
                  >
                    <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-brand-navy/40" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-2">
                    <h1 className="text-xl sm:text-3xl font-bold text-slate-800" data-testid="partner-name">{partner.name}</h1>
                    {(() => {
                      const pills = [];
                      if (partner.is_sponsor) pills.push('Sponsor');
                      if (partner.is_partner) pills.push('Partner');
                      if (partner.is_exhibitor) pills.push('Exhibitor');
                      if (!pills.length && partner.partner_type) pills.push(partner.partner_type);
                      return pills.map(p => (
                        <span key={p} className={`text-xs px-3 py-1 rounded-full font-semibold text-white ${typeColor[p] || typeColor.Partner}`} data-testid={`partner-detail-pill-${p.toLowerCase()}`}>
                          {p}
                        </span>
                      ));
                    })()}
                  </div>
                  {partner.tagline && <p className="text-brand-sage font-medium italic mb-3">{partner.tagline}</p>}
                  <RichText html={partner.description} className="text-slate-600 leading-relaxed" />
                  {partner.logo_description && (
                    <div className="text-xs text-slate-500 mt-3 border-l-2 border-brand-navy/20 pl-3 italic" data-testid="logo-description">
                      <RichText html={partner.logo_description} className="compact" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Details */}
              <Card className="p-4 sm:p-6" data-testid="company-details">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Company Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {partner.sector && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div><p className="text-xs text-slate-400">Sector</p><p className="text-sm font-medium text-slate-700">{partner.sector}</p></div>
                    </div>
                  )}
                  {(partner.hq_location || partner.hq_country) && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 flex items-center justify-center">
                        <MapPin className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div><p className="text-xs text-slate-400">Headquarters</p><p className="text-sm font-medium text-slate-700">{[partner.hq_location, partner.hq_country].filter(Boolean).join(', ')}</p></div>
                    </div>
                  )}
                  {partner.contact_email && (
                    <div className="flex items-center gap-3" data-testid="company-email">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-400">Email</p>
                        <a href={`mailto:${partner.contact_email}`} className="text-sm font-medium text-brand-navy hover:underline truncate block">{partner.contact_email}</a>
                      </div>
                    </div>
                  )}
                  {partner.company_size && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 flex items-center justify-center">
                        <Users className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div><p className="text-xs text-slate-400">Company Size</p><p className="text-sm font-medium text-slate-700">{partner.company_size} employees</p></div>
                    </div>
                  )}
                  {partner.year_founded && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 flex items-center justify-center">
                        <Calendar className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div><p className="text-xs text-slate-400">Founded</p><p className="text-sm font-medium text-slate-700">{partner.year_founded}</p></div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Sustainability Focus */}
              {partner.sustainability_focus?.length > 0 && (
                <Card className="p-4 sm:p-6" data-testid="sustainability-focus">
                  <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Leaf className="h-5 w-5 text-green-600" /> Sustainability Focus
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {partner.sustainability_focus.map(f => (
                      <Badge key={f} variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                        {f}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}

              {/* Categories / Expertise */}
              {partner.categories?.length > 0 && (
                <Card className="p-4 sm:p-6" data-testid="categories">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">Expertise</h2>
                  <div className="flex flex-wrap gap-2">
                    {partner.categories.map(c => (
                      <Badge key={c} variant="outline" className="border-brand-navy/20 text-brand-navy px-3 py-1">{c}</Badge>
                    ))}
                  </div>
                </Card>
              )}

              {/* Team Members */}
              {partner.team_members?.length > 0 && (
                <Card className="p-4 sm:p-6" data-testid="team-members">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Users className="h-5 w-5 text-brand-navy" /> Team on BriteHub
                    </h2>
                    <button onClick={() => navigate(`/partners/${sponsorId}/team`)}
                      className="text-xs font-medium text-brand-navy hover:underline" data-testid="view-all-team">
                      View all ({partner.team_members.length}) →
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {partner.team_members.slice(0, 6).map(m => (
                      <button key={m.user_id} onClick={() => setSelectedMember(m)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left" data-testid={`team-member-${m.user_id}`}>
                        {m.picture ? (
                          <MemberAvatar name={m.name} picture={m.picture} size={40} />
                        ) : (
                          <MemberAvatar name={m.name} size={40} />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
                          {m.profile?.role && <p className="text-xs text-slate-400 truncate">{m.profile.role}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* About Us */}
              {partner.about_sections?.length > 0 && (
                <Card className="p-4 sm:p-6" data-testid="about-us-section">
                  <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-brand-navy" /> Enabling Change
                  </h2>
                  <div className="space-y-4">
                    {partner.about_sections.map((sec, idx) => (
                      <div key={idx} className="border-l-2 border-brand-navy/20 pl-4" data-testid={`about-card-${idx}`}>
                        {sec.title && <h3 className="text-base font-semibold text-slate-800 mb-1">{sec.title}</h3>}
                        {sec.description && <RichText html={sec.description} className="text-sm text-slate-600 mb-2 compact" />}
                        {sec.media_url && sec.media_type === 'vimeo' && (
                          <div className="aspect-video rounded-lg overflow-hidden border border-slate-200 bg-black mt-2">
                            <iframe
                              src={(() => {
                                // Convert vimeo URL to embed if needed
                                const m = sec.media_url.match(/vimeo\.com\/(\d+)/);
                                return m ? `https://player.vimeo.com/video/${m[1]}` : sec.media_url;
                              })()}
                              title={sec.title || 'Vimeo video'} allowFullScreen className="w-full h-full" frameBorder="0" />
                          </div>
                        )}
                        {sec.media_url && sec.media_type !== 'vimeo' && (
                          <a href={sec.media_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-brand-navy hover:underline mt-1">
                            <FileText className="h-4 w-4" /> {sec.media_filename || 'View document'}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Latest News moved to the sidebar so the main column
                  isn't running noticeably longer than the right rail
                  on partners with short About/Team sections. */}
            </div>

            {/* Documents Section */}
            {documents.length > 0 && (
              <div className="lg:col-span-2">
                <Card className="p-6" data-testid="partner-documents">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-brand-navy" /> Documents
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {documents.map(doc => (
                      <div key={doc.doc_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-brand-navy/30 transition-colors"
                        data-testid={`partner-doc-${doc.doc_id}`}>
                        <div className="w-10 h-10 rounded-lg bg-brand-navy/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-brand-navy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{doc.title}</p>
                          {doc.description && <p className="text-xs text-slate-400 truncate">{doc.description}</p>}
                          <p className="text-xs text-slate-400">{(doc.size / 1024 / 1024).toFixed(1)} MB</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => {
                          fetch(`${BACKEND_URL}/api/partners/documents/${doc.doc_id}/download`, { credentials: 'include' })
                            .then(r => r.blob())
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = doc.filename;
                              document.body.appendChild(a); a.click();
                              window.URL.revokeObjectURL(url); document.body.removeChild(a);
                            }).catch(() => toast.error('Download failed'));
                        }} data-testid={`download-doc-${doc.doc_id}`} className="flex-shrink-0">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Contact & Links */}
              <Card className="p-5" data-testid="contact-card">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Connect</h3>
                <div className="space-y-2.5">
                  {partner.website && (
                    <a href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`}
                      target="_blank" rel="noopener noreferrer" className="block">
                      <Button variant="outline" className="w-full justify-start text-sm" data-testid="visit-website">
                        <Globe className="h-4 w-4 mr-2 text-brand-navy" /> Visit Website
                        <ExternalLink className="h-3 w-3 ml-auto text-slate-300" />
                      </Button>
                    </a>
                  )}
                  {partner.linkedin_url && (
                    <a href={partner.linkedin_url} target="_blank" rel="noopener noreferrer" className="block">
                      <Button variant="outline" className="w-full justify-start text-sm">
                        <Linkedin className="h-4 w-4 mr-2 text-[#0a66c2]" /> LinkedIn
                        <ExternalLink className="h-3 w-3 ml-auto text-slate-300" />
                      </Button>
                    </a>
                  )}
                  {partner.contact_email && (
                    <a href={`mailto:${partner.contact_email}`} className="block">
                      <Button variant="outline" className="w-full justify-start text-sm" data-testid="contact-email">
                        <Mail className="h-4 w-4 mr-2 text-brand-sage" /> Contact
                      </Button>
                    </a>
                  )}
                </div>
              </Card>

              {/* Latest News — moved from the main column into the sidebar
                  so the page balances on partners with shorter About/Team
                  sections. Stacks vertically because the sidebar is narrow. */}
              {partner.latest_news?.length > 0 && (
                <Card className="p-5" data-testid="latest-news-section">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-brand-navy" /> Latest News
                  </h3>
                  <div className="space-y-3">
                    {partner.latest_news.map((art, idx) => (
                      <a key={idx} href={art.article_url || '#'} target={art.article_url?.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                        className="block rounded-lg overflow-hidden border border-slate-200 hover:border-brand-navy/40 transition-colors group" data-testid={`news-article-${idx}`}>
                        {art.image_url ? (
                          <div className="aspect-video bg-slate-100">
                            <img src={art.image_url} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        ) : (
                          <div className="aspect-video bg-slate-100 flex items-center justify-center">
                            <FileText className="h-7 w-7 text-slate-300" />
                          </div>
                        )}
                        <div className="p-3">
                          <p className="text-sm font-semibold text-slate-800 line-clamp-2 mb-1">{art.title}</p>
                          {art.overview && <div className="text-xs text-slate-500"><RichText html={art.overview} className="card card-2" /></div>}
                        </div>
                      </a>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <MemberProfileModal
        member={selectedMember}
        open={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        user={user}
        onFollow={null}
        isFollowing={false}
      />
    </div>
  );
}
